import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MessageCircle, Send } from "lucide-react";

export default function AdminSupportChats() {
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const loadThreads = useCallback(async () => {
    try {
      const { data } = await api.get("/support/admin/threads");
      setThreads(data);
    } catch { /* polling — silent */ }
  }, []);
  const loadMessages = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/support/admin/threads/${id}`);
      setMessages(data);
      setThreads((prev) => prev.map((t) => (t.member_id === id ? { ...t, unread: 0 } : t)));
    } catch { /* polling — silent */ }
  }, []);

  useEffect(() => { loadThreads(); const t = setInterval(loadThreads, 12000); return () => clearInterval(t); }, [loadThreads]);
  useEffect(() => {
    if (!activeId) return;
    loadMessages(activeId);
    const t = setInterval(() => loadMessages(activeId), 7000);
    return () => clearInterval(t);
  }, [activeId, loadMessages]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const totalUnread = threads.reduce((s, t) => s + (t.unread || 0), 0);
  const active = threads.find((t) => t.member_id === activeId);

  const send = async () => {
    const body = input.trim();
    if (!body || !activeId || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/support/admin/threads/${activeId}`, { body });
      setMessages((m) => [...m, data]);
      setInput("");
      setThreads((prev) => prev.map((t) => (t.member_id === activeId
        ? { ...t, last_body: body, last_at: data.created_at, last_sender_role: "admin" }
        : t)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send the reply.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="glass overflow-hidden mb-6" data-testid="admin-chats-card">
      <div className="p-5 border-b border-black/5 flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center relative" style={{ background: "rgba(64,96,255,0.12)" }}>
          <MessageCircle size={16} style={{ color: "#4060FF" }} />
          {totalUnread > 0 && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center" style={{ background: "#DC2626" }} data-testid="chats-total-unread">
              {totalUnread}
            </span>
          )}
        </div>
        <div>
          <div className="kpi-label">Direct line · members → you</div>
          <h3 className="font-display text-xl font-semibold tracking-tight">Member questions</h3>
        </div>
        {totalUnread > 0 && (
          <span className="ml-auto chip" style={{ background: "rgba(220,38,38,0.10)", color: "#B91C1C", fontWeight: 700 }} data-testid="chats-unread-chip">
            {totalUnread} unanswered
          </span>
        )}
      </div>

      {threads.length === 0 ? (
        <div className="p-8 text-center text-sm text-ink-3" data-testid="chats-empty">
          No member questions yet. Members message you from the chat bubble in the bottom-right corner of their app.
        </div>
      ) : (
        <div className="flex" style={{ height: 380 }}>
          <div className="w-[280px] shrink-0 border-r border-black/5 overflow-y-auto">
            {threads.map((t) => (
              <button
                key={t.member_id}
                onClick={() => setActiveId(t.member_id)}
                className={`w-full text-left px-4 py-3 border-b border-black/5 transition-colors ${activeId === t.member_id ? "bg-white" : "hover:bg-white/50"}`}
                data-testid={`chat-thread-${t.member_id}`}
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm truncate flex-1">{t.member_name}</span>
                  {t.unread > 0 && (
                    <span className="min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center shrink-0" style={{ background: "#DC2626" }} data-testid={`chat-unread-${t.member_id}`}>
                      {t.unread}
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-ink-3 truncate mt-0.5">
                  {t.last_sender_role === "admin" ? "You: " : ""}{t.last_body}
                </div>
              </button>
            ))}
          </div>

          <div className="flex-1 flex flex-col min-w-0">
            {!activeId ? (
              <div className="flex-1 flex items-center justify-center text-sm text-ink-3" data-testid="chat-no-selection">
                Select a conversation to reply.
              </div>
            ) : (
              <>
                <div className="px-4 py-2.5 border-b border-black/5 text-xs">
                  <span className="font-semibold">{active?.member_name}</span>
                  <span className="text-ink-3 font-mono ml-2">{active?.member_email}</span>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="admin-chat-messages">
                  {messages.map((m) => (
                    <div key={m.id} className={`flex ${m.sender_role === "admin" ? "justify-end" : "justify-start"}`}>
                      <div
                        className="max-w-[70%] px-3 py-2 rounded-2xl text-xs leading-relaxed"
                        style={m.sender_role === "admin"
                          ? { background: "linear-gradient(135deg, #4060FF, #6B4FFF)", color: "white", borderBottomRightRadius: 6 }
                          : { background: "white", border: "1px solid rgba(15,18,38,0.08)", borderBottomLeftRadius: 6 }}
                      >
                        <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{m.body}</div>
                        <div className={`text-[9px] mt-1 ${m.sender_role === "admin" ? "text-white/60" : "text-ink-3"}`}>
                          {new Date(m.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={bottomRef} />
                </div>
                <div className="p-2.5 border-t border-black/5 flex items-center gap-2">
                  <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && send()}
                    placeholder={`Reply to ${active?.member_name || "member"}…`}
                    className="flex-1 text-xs px-3 py-2.5 rounded-xl outline-none"
                    style={{ background: "rgba(15,18,38,0.04)", border: "1px solid rgba(15,18,38,0.07)" }}
                    data-testid="admin-chat-input"
                  />
                  <button
                    onClick={send}
                    disabled={sending || !input.trim()}
                    className="w-9 h-9 rounded-xl text-white flex items-center justify-center disabled:opacity-40 shrink-0"
                    style={{ background: "linear-gradient(135deg, #4060FF, #6B4FFF)" }}
                    data-testid="admin-chat-send"
                  >
                    <Send size={14} />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
